// Google Analytics 4 utility functions
declare global {
  interface Window {
    gtag: (...args: any[]) => void;
  }
}

// GA4 Event parameters interface
interface GAEvent {
  action: string;
  category?: string;
  label?: string;
  value?: number;
  custom_parameters?: Record<string, any>;
}

// Check if gtag is available
const isGAAvailable = (): boolean => {
  return typeof window !== 'undefined' && typeof window.gtag === 'function';
};

// Track page views
export const trackPageView = (url: string, title?: string): void => {
  if (!isGAAvailable()) return;

  try {
    window.gtag('config', 'G-QFM14N5G7V', {
      page_location: url,
      page_title: title || document.title,
    });
  } catch (error) {
    console.warn('Analytics tracking error:', error);
  }
};

// Track custom events
export const trackEvent = ({ action, category, label, value, custom_parameters }: GAEvent): void => {
  if (!isGAAvailable()) return;

  try {
    window.gtag('event', action, {
      event_category: category,
      event_label: label,
      value: value,
      ...custom_parameters,
    });
  } catch (error) {
    console.warn('Analytics event tracking error:', error);
  }
};

// Track user identification (for authenticated users)
export const identifyUser = (userId: string, properties?: Record<string, any>): void => {
  if (!isGAAvailable()) return;

  try {
    window.gtag('config', 'G-QFM14N5G7V', {
      user_id: userId,
      custom_map: properties,
    });
  } catch (error) {
    console.warn('Analytics user identification error:', error);
  }
};

// Predefined event tracking functions for common actions
export const analytics = {
  // Job search events
  trackJobSearch: (searchTerm: string, filters?: Record<string, any>) => {
    trackEvent({
      action: 'job_search',
      category: 'Jobs',
      label: searchTerm,
      custom_parameters: { filters }
    });
  },

  trackJobView: (jobId: string, jobTitle: string) => {
    trackEvent({
      action: 'job_view',
      category: 'Jobs',
      label: jobTitle,
      custom_parameters: { job_id: jobId }
    });
  },

  trackJobApply: (jobId: string, jobTitle: string, source: string) => {
    trackEvent({
      action: 'job_apply',
      category: 'Jobs',
      label: jobTitle,
      custom_parameters: { job_id: jobId, source }
    });
  },

  // User engagement events
  trackResumeUpload: (fileType: string) => {
    trackEvent({
      action: 'resume_upload',
      category: 'Profile',
      label: fileType
    });
  },

  trackResumeTailor: (jobTitle: string) => {
    trackEvent({
      action: 'resume_tailor',
      category: 'Tools',
      label: jobTitle
    });
  },

  trackLinkedInOptimization: (method: string) => {
    trackEvent({
      action: 'linkedin_optimization',
      category: 'Tools',
      label: method
    });
  },

  trackInterviewPrep: (prepType: string, jobTitle?: string) => {
    trackEvent({
      action: 'interview_prep',
      category: 'Tools',
      label: prepType,
      custom_parameters: { job_title: jobTitle }
    });
  },

  // Authentication events
  trackSignUp: (method: string) => {
    trackEvent({
      action: 'sign_up',
      category: 'Auth',
      label: method
    });
  },

  trackSignIn: (method: string) => {
    trackEvent({
      action: 'sign_in',
      category: 'Auth',
      label: method
    });
  },

  trackProfileComplete: () => {
    trackEvent({
      action: 'profile_complete',
      category: 'Profile'
    });
  },

  // Form events
  trackFormSubmit: (formName: string, success: boolean) => {
    trackEvent({
      action: 'form_submit',
      category: 'Forms',
      label: formName,
      custom_parameters: { success }
    });
  },

  // Feature usage
  trackFeatureUsage: (featureName: string, context?: string) => {
    trackEvent({
      action: 'feature_usage',
      category: 'Features',
      label: featureName,
      custom_parameters: { context }
    });
  }
};